(function (root) {
  'use strict';

  const namespace = root.Kate3DPreview = root.Kate3DPreview || {};
  if (namespace.create) return;

  const parseConfig = (element) => {
    const configElement = element.querySelector('[data-three-d-preview-config]');
    if (!configElement) throw new Error('3D preview configuration is missing.');
    return JSON.parse(configElement.textContent);
  };

  namespace.create = function create3DPreview(rootElement) {
    const config = parseConfig(rootElement);
    const catalog = new namespace.CatalogService(config);
    const historyStore = new namespace.HistoryStore();
    const cartService = new namespace.CartService();
    const urlParams = new URLSearchParams(root.location.search);
    const requestedSource = (urlParams.get('source') || '').trim();
    const attributionSource = requestedSource === 'recommended_floors' ? requestedSource : '3d_preview';
    const normalizeVariantId = (value) => {
      const normalized = String(value || '').trim();
      if (/^\d+$/.test(normalized)) return normalized;
      const gidMatch = normalized.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/);
      return gidMatch?.[1] || '';
    };
    const requestedBackdropVariantId = normalizeVariantId(urlParams.get('backdrop_variant'));
    const requestedFloorVariantId = normalizeVariantId(urlParams.get('floor_variant'));
    const requestedBackdropHandle = (urlParams.get('backdrop') || '').trim();
    const requestedBackdropImage = (urlParams.get('backdrop_image') || '').trim();
    const requestedFloorHandle = (urlParams.get('floor') || '').trim();
    const requestedFloorImage = (urlParams.get('floor_image') || '').trim();
    const validBackdropHandle = /^[a-z0-9][a-z0-9-]*$/i.test(requestedBackdropHandle);
    const validFloorHandle = /^[a-z0-9][a-z0-9-]*$/i.test(requestedFloorHandle);
    let destroyed = false;
    let quickBuyLoadingCleanup = null;

    return {
      activeTab: 'wall',
      wallDefaultProduct: {},
      floorDefaultProduct: {},
      wallCurrentProduct: {},
      floorCurrentProduct: {},
      wallProductImages: [],
      floorProductImages: [],
      wallSelectedImageIndex: 0,
      floorSelectedImageIndex: 0,
      galleryExpanded: { wall: false, floor: false },
      galleryImagesEnabled: { wall: false, floor: false },
      galleryLoading: { wall: false, floor: false },
      galleryRequestIds: { wall: 0, floor: 0 },
      galleryInitialRevealComplete: false,
      wallProducts: [],
      floorProducts: [],
      floorProductsLoaded: false,
      wallCurrentPage: 1,
      wallTotalPages: 1,
      wallHasNextPage: false,
      wallHasPreviousPage: false,
      floorCurrentPage: 1,
      floorTotalPages: 1,
      floorHasNextPage: false,
      floorHasPreviousPage: false,
      searchQuery: '',
      searchProducts: [],
      searchTimeout: null,
      searchCurrentPage: 1,
      searchTotalPages: 1,
      searchHasNextPage: false,
      searchHasPreviousPage: false,
      searchRequestId: 0,
      showSearchResults: false,
      showHistoryMode: false,
      previewHistory: { wall: [], floor: [] },
      previewHistoryId: [],
      isPreviewLoading: true,
      isCatalogLoading: false,
      isAddingToCart: false,
      quickBuyLoadingType: '',
      quickBuyLoadingPosition: { x: 0, y: 0 },
      sceneRendererReady: false,
      sceneStatus: '',
      sceneTextureRequestIds: { wall: 0, floor: 0 },
      sceneTransitioning: { wall: false, floor: false },

      async init() {
        this.hydrateInitialProducts({
          skipWall: Boolean(requestedBackdropVariantId || validBackdropHandle),
          skipFloor: Boolean(requestedFloorVariantId || validFloorHandle),
        });
        this.loadFromCache();
        await this.$nextTick();
        if (this.hasValidWallProduct && this.hasValidFloorProduct) {
          this.isPreviewLoading = false;
        }

        const rendererPromise = this.initializeSceneRenderer()
          .then(() => this.syncSceneTextures())
          .catch((error) => {
            if (destroyed) return;
            console.warn('Unable to enhance the 3D preview with PixiJS:', error);
          });

        try {
          await catalog.init();

          const applyRequestedImage = (product, requestedImage) => {
            if (!product) return null;
            const requestedProduct = { ...product };
            if (
              /^https?:\/\//i.test(requestedImage)
              || requestedImage.startsWith('//')
              || requestedImage.startsWith('/cdn/')
            ) {
              let normalizedImage = requestedImage;
              try {
                const parsedImageUrl = new URL(requestedImage, root.location.origin);
                if (parsedImageUrl.pathname.startsWith('/cdn/')) {
                  normalizedImage = `${parsedImageUrl.pathname}${parsedImageUrl.search}`;
                }
              } catch (error) {
                normalizedImage = requestedImage;
              }

              requestedProduct.image = normalizedImage;
              requestedProduct.images = [
                { url: normalizedImage, alt: requestedProduct.title || '' },
                ...(requestedProduct.images || []).filter((image) => (image.url || image.src) !== normalizedImage),
              ];
            }
            return requestedProduct;
          };

          if (requestedBackdropVariantId || requestedFloorVariantId) {
            const requestedProducts = await catalog.loadProductsByVariantIds({
              wallVariantId: requestedBackdropVariantId,
              floorVariantId: requestedFloorVariantId,
            });
            if (requestedProducts.wall) this.recordDefaultProduct('wall', requestedProducts.wall);
            if (requestedProducts.floor) this.recordDefaultProduct('floor', requestedProducts.floor);
          }

          if (
            (validBackdropHandle && !this.hasValidWallProduct)
            || (validFloorHandle && !this.hasValidFloorProduct)
          ) {
            const requestedProducts = await catalog.loadDefaultProducts({
              wallHandle: validBackdropHandle && !this.hasValidWallProduct ? requestedBackdropHandle : '',
              floorHandle: validFloorHandle && !this.hasValidFloorProduct ? requestedFloorHandle : '',
            });

            if (requestedProducts.wall) {
              this.recordDefaultProduct('wall', applyRequestedImage(requestedProducts.wall, requestedBackdropImage));
            }
            if (requestedProducts.floor) {
              this.recordDefaultProduct('floor', applyRequestedImage(requestedProducts.floor, requestedFloorImage));
            }
          }

          if (!this.hasValidWallProduct || !this.hasValidFloorProduct) {
            const defaults = await catalog.loadDefaultProducts({
              wallHandle: this.hasValidWallProduct ? '' : config.wallDefaultProductHandle,
              floorHandle: this.hasValidFloorProduct ? '' : config.floorDefaultProductHandle,
            });
            if (defaults.wall) this.recordDefaultProduct('wall', defaults.wall);
            if (defaults.floor) this.recordDefaultProduct('floor', defaults.floor);
            await this.$nextTick();
            if (this.hasValidWallProduct && this.hasValidFloorProduct) this.isPreviewLoading = false;
          }

          this.isCatalogLoading = true;
          await this.loadWallCollectionProducts(config.wallCollectionHandle, 1);
          this.setFallbackProducts();
        } catch (error) {
          if (!destroyed) {
            console.error('Unable to initialize the 3D preview catalog:', error);
            this.sceneStatus = this.sceneStatus || 'Product choices are temporarily unavailable.';
          }
        } finally {
          if (!destroyed) {
            this.isCatalogLoading = false;
            this.isPreviewLoading = false;
            await this.$nextTick();
            this.revealInitialProductGalleries();
          }
        }

        await rendererPromise;
      },

      hydrateInitialProducts({ skipWall = false, skipFloor = false } = {}) {
        if (!skipWall && config.initialWallProduct?.id) this.recordDefaultProduct('wall', config.initialWallProduct);
        if (!skipFloor && config.initialFloorProduct?.id) this.recordDefaultProduct('floor', config.initialFloorProduct);
      },

      async initializeSceneRenderer() {
        if (!root.Kate3DPreviewRenderer || !this.$refs.sceneContainer || !this.$refs.sceneCanvas) {
          this.sceneStatus = 'Interactive preview is using compatibility mode.';
          return;
        }
        try {
          await root.Kate3DPreviewRenderer.ensurePixi(config.pixiAssetUrl);
          if (destroyed) return;
          const renderer = new root.Kate3DPreviewRenderer({
            container: this.$refs.sceneContainer,
            canvas: this.$refs.sceneCanvas,
            onError: (error) => {
              if (destroyed) return;
              console.warn('PixiJS rendering failed; using the compatibility preview:', error);
              this.sceneRendererReady = false;
              this.sceneStatus = 'Interactive preview is using compatibility mode.';
            },
          });
          this.$refs.sceneContainer._kate3DPreviewRenderer = renderer;
          await renderer.init();
        } catch (error) {
          console.warn('PixiJS initialization failed; keeping the compatibility preview:', error);
          this.getSceneRenderer()?.destroy();
          if (this.$refs.sceneContainer) this.$refs.sceneContainer._kate3DPreviewRenderer = null;
          this.sceneRendererReady = false;
          this.sceneStatus = 'Interactive preview is using compatibility mode.';
        }
      },

      getSceneRenderer() {
        return this.$refs.sceneContainer?._kate3DPreviewRenderer || null;
      },

      async syncSceneTextures() {
        const renderer = this.getSceneRenderer();
        if (!renderer) return;
        const results = await Promise.allSettled([
          this.updateSceneTexture('wall', this.wallCurrentProduct),
          this.updateSceneTexture('floor', this.floorCurrentProduct),
        ]);
        if (destroyed) return;
        this.sceneRendererReady = !results.some((result) => result.status === 'rejected') && renderer.isReady();
        if (this.sceneRendererReady) this.sceneStatus = this.getSceneReadyStatus();
      },

      async updateSceneTexture(type, product) {
        const requestId = ++this.sceneTextureRequestIds[type];
        this.sceneTransitioning[type] = true;
        this.sceneStatus = `Updating the selected ${type === 'wall' ? 'backdrop' : 'floor'} preview...`;

        const imageUrl = this.getSelectedProductImage(type, product);
        const renderer = this.getSceneRenderer();
        if (!product?.id || !imageUrl) {
          this.finishSceneTransition(type, requestId);
          return { applied: false };
        }

        const setter = type === 'wall' ? 'setWallTexture' : 'setFloorTexture';
        try {
          await this.preloadSceneImage(imageUrl);
          if (destroyed || requestId !== this.sceneTextureRequestIds[type]) {
            return { applied: false, stale: true };
          }

          const result = renderer
            ? await renderer[setter](imageUrl)
            : { applied: false, stale: false };
          if (destroyed || requestId !== this.sceneTextureRequestIds[type] || result?.stale) {
            return { applied: false, stale: true };
          }
          this.sceneRendererReady = Boolean(renderer?.isReady());
          this.finishSceneTransition(type, requestId, this.getSceneReadyStatus());
          return result;
        } catch (error) {
          if (destroyed || requestId !== this.sceneTextureRequestIds[type]) {
            return { applied: false, stale: true };
          }
          this.sceneRendererReady = false;
          this.finishSceneTransition(
            type,
            requestId,
            `Unable to render the selected ${type}; compatibility preview is active.`
          );
          console.warn(`Unable to update the ${type} texture:`, error);
          throw error;
        }
      },

      preloadSceneImage(url) {
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.decoding = 'async';
          image.onload = () => resolve(url);
          image.onerror = () => reject(new Error('Unable to load the selected preview image.'));
          image.src = url;
          if (image.complete && image.naturalWidth > 0) resolve(url);
        });
      },

      finishSceneTransition(type, requestId, status = '') {
        if (destroyed || requestId !== this.sceneTextureRequestIds[type]) return;
        this.sceneTransitioning[type] = false;
        if (status) this.sceneStatus = status;
      },

      getSceneReadyStatus() {
        const wallTitle = this.wallCurrentProduct?.title || 'selected backdrop';
        const floorTitle = this.floorCurrentProduct?.title || 'selected floor';
        return `Preview updated: ${wallTitle} with ${floorTitle}.`;
      },

      async loadWallCollectionProducts(collectionHandle, page = 1) {
        const snapshot = await catalog.loadWallPage(collectionHandle, page);
        this.wallProducts = snapshot.products;
        this.wallCurrentPage = snapshot.page;
        this.wallHasNextPage = snapshot.hasNextPage;
        this.wallHasPreviousPage = snapshot.hasPreviousPage;
        this.wallTotalPages = Math.max(this.wallTotalPages, snapshot.page + (snapshot.hasNextPage ? 1 : 0));
        return snapshot;
      },

      async loadFloorCollectionProducts(collectionHandle, page = 1) {
        const snapshot = await catalog.loadFloorPage(collectionHandle, page);
        this.floorProducts = snapshot.products;
        this.floorCurrentPage = snapshot.page;
        this.floorHasNextPage = snapshot.hasNextPage;
        this.floorHasPreviousPage = snapshot.hasPreviousPage;
        this.floorTotalPages = Math.max(this.floorTotalPages, snapshot.page + (snapshot.hasNextPage ? 1 : 0));
        return snapshot;
      },

      async changePage(type, page) {
        if (page < 1 || this.isCatalogLoading) return;
        if (type === 'wall' && page > this.wallCurrentPage && !this.wallHasNextPage) return;
        if (type === 'floor' && page > this.floorCurrentPage && !this.floorHasNextPage) return;

        this.isCatalogLoading = true;
        try {
          if (type === 'wall') {
            await this.loadWallCollectionProducts(config.wallCollectionHandle, page);
          } else if (type === 'floor') {
            await this.loadFloorCollectionProducts(config.floorCollectionHandle, page);
          }
        } catch (error) {
          console.error(`Unable to load ${type} page ${page}:`, error);
        } finally {
          if (!destroyed) this.isCatalogLoading = false;
        }
      },

      selectProduct(type, product) {
        this.applyProduct(type, product, { trackHistory: true, hydrateGallery: true });
      },

      applyProduct(type, product, { trackHistory = false, hydrateGallery = false } = {}) {
        if (!['wall', 'floor'].includes(type) || !product?.id) return;

        this.galleryImagesEnabled[type] = this.galleryExpanded[type];

        if (type === 'wall') {
          this.wallCurrentProduct = product;
          this.wallProductImages = this.normalizeProductImages(product);
          this.wallSelectedImageIndex = 0;
        } else {
          this.floorCurrentProduct = product;
          this.floorProductImages = this.normalizeProductImages(product);
          this.floorSelectedImageIndex = 0;
        }

        if (trackHistory) this.addToPreviewHistory(type, product);
        this.updateSceneTexture(type, product).catch(() => {});
        this.$nextTick(() => this.resetGalleryScroll(type));
        if (hydrateGallery) this.hydrateProductGallery(type, product);
      },

      setFallbackProducts() {
        if (!this.wallCurrentProduct?.id && this.wallProducts.length) {
          this.recordDefaultProduct('wall', this.wallProducts[0]);
        }
        if (!this.floorCurrentProduct?.id && this.floorProducts.length) {
          this.recordDefaultProduct('floor', this.floorProducts[0]);
        }
      },

      recordDefaultProduct(type, product) {
        if (!product?.id) return;
        if (type === 'wall') {
          this.wallDefaultProduct = product;
        } else if (type === 'floor') {
          this.floorDefaultProduct = product;
        }
        catalog.seedProductGallery(product);
        this.applyProduct(type, product);
      },

      getProductImage(product) {
        if (!product?.id) return '';
        if (typeof product.image === 'string') return product.image;
        if (product.image?.url) return product.image.url;
        if (Array.isArray(product.images) && product.images.length) {
          return product.images[0]?.url || product.images[0]?.src || '';
        }
        return '';
      },

      normalizeProductImages(product) {
        const seen = new Set();
        const images = Array.isArray(product?.images) ? product.images : [];
        const normalized = images
          .map((image) => {
            if (typeof image === 'string') return { url: image, alt: product?.title || '' };
            return {
              url: image?.url || image?.src || '',
              alt: image?.alt || image?.altText || product?.title || '',
              width: image?.width || null,
              height: image?.height || null,
            };
          })
          .filter((image) => {
            if (!image.url || seen.has(image.url)) return false;
            seen.add(image.url);
            return true;
          });

        const fallback = this.getProductImage(product);
        if (!normalized.length && fallback) {
          normalized.push({ url: fallback, alt: product?.title || '', width: null, height: null });
        }
        return normalized;
      },

      getProductImages(type) {
        return type === 'wall' ? this.wallProductImages : this.floorProductImages;
      },

      getSelectedProductImage(type, product) {
        const currentProduct = type === 'wall' ? this.wallCurrentProduct : this.floorCurrentProduct;
        if (!product?.id || currentProduct?.id !== product.id) return this.getProductImage(product);
        const images = this.getProductImages(type);
        const index = type === 'wall' ? this.wallSelectedImageIndex : this.floorSelectedImageIndex;
        return images[index]?.url || images[0]?.url || this.getProductImage(product);
      },

      getThumbnailImageUrl(image) {
        const sourceUrl = typeof image === 'string' ? image : image?.url || image?.src || '';
        if (!sourceUrl) return '';
        try {
          const url = new URL(sourceUrl, document.baseURI);
          if (url.hostname.endsWith('cdn.shopify.com')) url.searchParams.set('width', '120');
          return url.href;
        } catch (error) {
          return sourceUrl;
        }
      },

      async hydrateProductGallery(type, product) {
        if (!product?.handle) return;
        const requestId = ++this.galleryRequestIds[type];
        this.galleryLoading[type] = true;
        try {
          const images = await catalog.loadProductGallery(product);
          const currentProduct = type === 'wall' ? this.wallCurrentProduct : this.floorCurrentProduct;
          if (destroyed || requestId !== this.galleryRequestIds[type] || currentProduct?.handle !== product.handle) return;
          if (images.length) {
            if (type === 'wall') {
              this.wallProductImages = this.normalizeProductImages({ ...product, images });
              this.wallSelectedImageIndex = 0;
            } else {
              this.floorProductImages = this.normalizeProductImages({ ...product, images });
              this.floorSelectedImageIndex = 0;
            }
            await this.$nextTick();
            this.resetGalleryScroll(type);
            this.updateSceneTexture(type, currentProduct).catch(() => {});
          }
        } catch (error) {
          if (requestId === this.galleryRequestIds[type]) {
            console.warn(`Unable to load the ${type} product gallery:`, error);
          }
        } finally {
          if (!destroyed && requestId === this.galleryRequestIds[type]) this.galleryLoading[type] = false;
        }
      },

      selectProductImage(type, index, { focus = false } = {}) {
        const images = this.getProductImages(type);
        if (!Number.isInteger(index) || index < 0 || index >= images.length) return;
        if (type === 'wall') this.wallSelectedImageIndex = index;
        if (type === 'floor') this.floorSelectedImageIndex = index;

        const product = type === 'wall' ? this.wallCurrentProduct : this.floorCurrentProduct;
        this.updateSceneTexture(type, product).catch(() => {});
        this.$nextTick(() => {
          this.centerSelectedThumbnail(type, index, true);
          if (focus) {
            this.$refs[`${type}Gallery`]
              ?.querySelector(`[data-image-index="${index}"]`)
              ?.focus();
          }
        });
      },

      handleProductImageKeydown(event, type, index) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const images = this.getProductImages(type);
        if (!images.length) return;
        const nextIndex = event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? images.length - 1
            : event.key === 'ArrowLeft'
              ? Math.max(0, index - 1)
              : Math.min(images.length - 1, index + 1);
        this.selectProductImage(type, nextIndex, { focus: true });
      },

      setProductGalleryExpanded(type, expanded) {
        if (!['wall', 'floor'].includes(type)) return;
        const nextExpanded = Boolean(expanded);
        if (nextExpanded) this.galleryImagesEnabled[type] = true;
        this.galleryExpanded[type] = nextExpanded;
      },

      supportsProductGalleryHover() {
        return Boolean(root.matchMedia?.('(hover: hover) and (pointer: fine)').matches);
      },

      revealInitialProductGalleries() {
        if (this.galleryInitialRevealComplete) return;
        this.galleryInitialRevealComplete = true;
        if (!this.supportsProductGalleryHover()) return;

        ['wall', 'floor'].forEach((type) => {
          if (this.getProductImages(type).length) this.setProductGalleryExpanded(type, true);
        });
      },

      handleProductGalleryPointerEnter(event, type) {
        if (!this.supportsProductGalleryHover()) return;
        if (event.pointerType && event.pointerType !== 'mouse') return;
        this.setProductGalleryExpanded(type, true);
      },

      handleProductGalleryPointerLeave(event, type) {
        if (!this.supportsProductGalleryHover()) return;
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const gallery = event.currentTarget;
        const activeElement = document.activeElement;
        const toggle = gallery?.querySelector('.scene-product-gallery__toggle');
        if (gallery?.contains(activeElement) && activeElement !== toggle) {
          toggle?.focus({ preventScroll: true });
        }
        this.setProductGalleryExpanded(type, false);
      },

      handleProductGalleryToggle(event, type) {
        const pointerType = event.pointerType || '';
        const isDesktopMouseClick = this.supportsProductGalleryHover()
          && (pointerType === 'mouse' || (!pointerType && event.detail > 0));
        if (isDesktopMouseClick) return;
        this.setProductGalleryExpanded(type, !this.galleryExpanded[type]);
      },

      handleProductGalleryFocusOut(event, type) {
        if (event.currentTarget?.contains(event.relatedTarget)) return;
        this.setProductGalleryExpanded(type, false);
      },

      resetGalleryScroll(type) {
        const container = this.$refs[`${type}Gallery`];
        if (!container) return;
        container.scrollLeft = 0;
      },

      centerSelectedThumbnail(type, index, smooth = true) {
        const container = this.$refs[`${type}Gallery`];
        const thumbnail = container?.querySelector(`[data-image-index="${index}"]`);
        if (!container || !thumbnail) return;
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const targetLeft = container.scrollLeft
          + (thumbnailRect.left + (thumbnailRect.width / 2))
          - (containerRect.left + (containerRect.width / 2));
        const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
        const clampedLeft = Math.min(maxScrollLeft, Math.max(0, targetLeft));
        container.scrollTo({ left: clampedLeft, behavior: smooth ? 'smooth' : 'auto' });
      },

      generateProductUrl(product) {
        if (!product?.handle) return '';
        const productUrl = new URL(
          `${root.Shopify?.routes?.root || '/'}products/${product.handle}`,
          root.location.origin,
        );
        const variantId = String(product.selectedVariantId || product.selectedVariant?.id || '')
          .match(/(\d+)$/)?.[1];
        if (variantId) productUrl.searchParams.set('variant', variantId);
        return `${productUrl.pathname}${productUrl.search}`;
      },

      setQuickBuyLoadingPosition(type, event) {
        const scene = this.$refs.sceneContainer;
        const hotspot = event?.currentTarget || this.$refs[`${type}Hotspot`];
        if (!scene || !hotspot) return;

        const sceneRect = scene.getBoundingClientRect();
        const hotspotRect = hotspot.getBoundingClientRect();
        const hasPointerCoordinates = event?.detail > 0
          && Number.isFinite(event.clientX)
          && Number.isFinite(event.clientY);
        const clientX = hasPointerCoordinates ? event.clientX : hotspotRect.left + (hotspotRect.width / 2);
        const clientY = hasPointerCoordinates ? event.clientY : hotspotRect.top + (hotspotRect.height / 2);
        const loaderRadius = 16;

        this.quickBuyLoadingPosition = {
          x: Math.min(sceneRect.width - loaderRadius, Math.max(loaderRadius, clientX - sceneRect.left)),
          y: Math.min(sceneRect.height - loaderRadius, Math.max(loaderRadius, clientY - sceneRect.top)),
        };
      },

      async openQuickBuyDrawer(type, event = null) {
        if (this.quickBuyLoadingType) return;

        const product = type === 'wall' ? this.wallCurrentProduct : this.floorCurrentProduct;
        const drawer = type === 'wall' ? this.$refs.wallDrawer : this.$refs.floorDrawer;
        const productUrl = this.generateProductUrl(product);
        if (!drawer || !productUrl) return;

        this.setQuickBuyLoadingPosition(type, event);
        this.quickBuyLoadingType = type;
        this.sceneStatus = `Loading options for ${product.title || `${type} product`}...`;

        let settled = false;
        let loadingTimeout = null;
        const releaseLoadingListeners = () => {
          drawer.removeEventListener('openable-element:load:end', handleReady);
          drawer.removeEventListener('openable-element:open', handleReady);
          if (loadingTimeout) clearTimeout(loadingTimeout);
          loadingTimeout = null;
          if (quickBuyLoadingCleanup === releaseLoadingListeners) quickBuyLoadingCleanup = null;
        };
        const finishLoading = (message = '') => {
          if (settled) return;
          settled = true;
          releaseLoadingListeners();
          if (destroyed || this.quickBuyLoadingType !== type) return;
          this.quickBuyLoadingType = '';
          if (message) this.sceneStatus = message;
        };
        const handleReady = () => {
          finishLoading(`Options ready for ${product.title || `${type} product`}.`);
        };

        drawer.addEventListener('openable-element:load:end', handleReady);
        drawer.addEventListener('openable-element:open', handleReady);
        loadingTimeout = setTimeout(() => {
          finishLoading('Unable to load product options. Please try again.');
        }, 15000);
        quickBuyLoadingCleanup = releaseLoadingListeners;

        try {
          await customElements.whenDefined('quick-buy-drawer');
          if (destroyed || this.quickBuyLoadingType !== type) {
            releaseLoadingListeners();
            return;
          }
          const quickBuyUrl = new URL(productUrl, root.location.origin);
          quickBuyUrl.searchParams.set('view', 'quick-buy-drawer');
          drawer.setAttribute('href', `${quickBuyUrl.pathname}${quickBuyUrl.search}`);
          drawer.open = true;
        } catch (error) {
          finishLoading('Unable to load product options. Please try again.');
          console.error(`Unable to open the ${type} quick buy drawer:`, error);
        }
      },

      async searchProductsAPI(query, page = 1, requestId = this.searchRequestId) {
        this.isCatalogLoading = true;
        try {
          const snapshot = await catalog.search(query, page);
          if (destroyed || snapshot.stale || requestId !== this.searchRequestId || snapshot.query !== this.searchQuery.trim()) return;
          this.searchProducts = snapshot.products;
          this.searchCurrentPage = snapshot.page;
          this.searchHasNextPage = snapshot.hasNextPage;
          this.searchHasPreviousPage = snapshot.hasPreviousPage;
          this.searchTotalPages = Math.max(this.searchTotalPages, snapshot.page + (snapshot.hasNextPage ? 1 : 0));
          this.showSearchResults = true;
        } catch (error) {
          if (requestId === this.searchRequestId) {
            console.error('Unable to search products:', error);
            this.searchProducts = [];
            this.showSearchResults = false;
          }
        } finally {
          if (!destroyed && requestId === this.searchRequestId) this.isCatalogLoading = false;
        }
      },

      onSearchInput(event) {
        const query = event.target.value;
        this.searchQuery = query;
        const requestId = ++this.searchRequestId;
        if (this.searchTimeout) clearTimeout(this.searchTimeout);

        this.searchProducts = [];
        this.searchCurrentPage = 1;
        this.searchTotalPages = 1;
        this.searchHasNextPage = false;
        this.searchHasPreviousPage = false;
        catalog.resetSearch(query);

        if (!query.trim()) {
          this.showSearchResults = false;
          this.isCatalogLoading = false;
          return;
        }

        this.searchTimeout = setTimeout(() => {
          this.searchProductsAPI(query, 1, requestId);
        }, 300);
      },

      async changeSearchPage(page) {
        if (page < 1 || !this.searchQuery.trim() || this.isCatalogLoading) return;
        if (page > this.searchCurrentPage && !this.searchHasNextPage) return;
        await this.searchProductsAPI(this.searchQuery, page, this.searchRequestId);
      },

      selectSearchProduct(product) {
        this.selectProduct(this.activeTab, product);
      },

      async switchTab(tab) {
        if (!['wall', 'floor'].includes(tab)) return;
        this.activeTab = tab;
        if (tab !== 'floor' || this.floorProductsLoaded) return;

        this.isCatalogLoading = true;
        try {
          await this.loadFloorCollectionProducts(config.floorCollectionHandle, 1);
          this.floorProductsLoaded = true;
          this.setFallbackProducts();
        } catch (error) {
          console.error('Unable to load floor products:', error);
        } finally {
          if (!destroyed) this.isCatalogLoading = false;
        }
      },

      handleTabKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const nextTab = event.key === 'Home'
          ? 'wall'
          : event.key === 'End'
            ? 'floor'
            : this.activeTab === 'wall' ? 'floor' : 'wall';
        this.switchTab(nextTab).then(() => {
          this.$nextTick(() => this.$refs[`${nextTab}Tab`]?.focus());
        });
      },

      toggleHistoryMode() {
        this.showHistoryMode = !this.showHistoryMode;
      },

      applyHistory(snapshot) {
        this.previewHistory = snapshot.previewHistory;
        this.previewHistoryId = snapshot.previewHistoryId;
      },

      addToPreviewHistory(type, product) {
        if (!product?.id) return;
        this.applyHistory(historyStore.add(type, product, this.getProductImage(product)));
      },

      loadFromCache() {
        this.applyHistory(historyStore.load());
      },

      clearCache() {
        this.applyHistory(historyStore.clear());
      },

      async addComboToCart() {
        this.isAddingToCart = true;
        try {
          if (!this.hasValidWallProduct || !this.hasValidFloorProduct) {
            throw new Error('Please select both a backdrop and a floor.');
          }
          await cartService.addCombo(this.wallCurrentProduct, this.floorCurrentProduct, {
            source: attributionSource,
            path: '3d_preview',
          });
          if (typeof root.gtag === 'function') {
            root.gtag('event', attributionSource === 'recommended_floors'
              ? 'recommended_floors_3d_combo_add_success'
              : 'three_d_preview_combo_add_success', {
              product_handle: this.wallCurrentProduct.handle || '',
              floor_handle: this.floorCurrentProduct.handle || '',
              combo_source: attributionSource,
            });
          }
          cartService.notify(rootElement, 'success');
        } catch (error) {
          console.error('Unable to add the 3D preview combination to the cart:', error);
          cartService.notify(rootElement, 'error', error.message || 'Unable to add this combination to the cart.');
        } finally {
          if (!destroyed) this.isAddingToCart = false;
        }
      },

      continueShopping() {
        const routesRoot = root.Shopify?.routes?.root || '/';
        root.location.href = `${routesRoot}collections/${config.continueShoppingHandle || 'best-selling'}`;
      },

      resetToDefault() {
        if (this.wallDefaultProduct?.id) this.applyProduct('wall', this.wallDefaultProduct);
        if (this.floorDefaultProduct?.id) this.applyProduct('floor', this.floorDefaultProduct);
      },

      destroy() {
        if (destroyed) return;
        destroyed = true;
        quickBuyLoadingCleanup?.();
        quickBuyLoadingCleanup = null;
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchRequestId += 1;
        this.sceneTextureRequestIds.wall += 1;
        this.sceneTextureRequestIds.floor += 1;
        this.sceneTransitioning.wall = false;
        this.sceneTransitioning.floor = false;
        this.galleryRequestIds.wall += 1;
        this.galleryRequestIds.floor += 1;
        catalog.destroy();
        this.getSceneRenderer()?.destroy();
        if (this.$refs.sceneContainer) this.$refs.sceneContainer._kate3DPreviewRenderer = null;
        this.sceneRendererReady = false;
      },

      get wallProductImageUrl() {
        return this.getSelectedProductImage('wall', this.wallCurrentProduct);
      },

      get floorProductImageUrl() {
        return this.getSelectedProductImage('floor', this.floorCurrentProduct);
      },

      get isSceneTransitioning() {
        return this.sceneTransitioning.wall || this.sceneTransitioning.floor;
      },

      get hasValidWallProduct() {
        return Boolean(this.wallCurrentProduct?.id);
      },

      get hasValidFloorProduct() {
        return Boolean(this.floorCurrentProduct?.id);
      },
    };
  };
})(window);
